import { useEffect, useState } from "react";

import { ExternalLink, FileText } from "lucide-react";
import PropTypes from "prop-types";
import Modal from "@components/ui-components/modal";
import LoadingButton from "@components/ui-components/loading-button";
import { formatFileSize } from "@panel/shared/contract/misc/helpers";
import { CONFIRM_NOTE, CONFIRM_TITLE, PREVIEW_KIND } from "../misc/constants";

const FRAME_CLASS = "grid min-h-[160px] w-full place-items-center overflow-hidden rounded-lg border border-gray-200 bg-light/60 p-3";

const Detail = ({ label, value }) => (
    <div className="min-w-0">
        <dt className="text-xs uppercase tracking-wide text-muted">{label}</dt>
        <dd className="mt-0.5 break-words text-sm text-gray-700">{value}</dd>
    </div>
);

Detail.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.node
};

// Images are drawn from a local object URL rather than the stored file — the
// bytes are already on this machine, so the preview needs no second download.
const useObjectUrl = (file) => {
    const [url, setUrl] = useState("");

    useEffect(() => {
        if (!file) return undefined;

        const objectUrl = URL.createObjectURL(file);
        setUrl(objectUrl);
        return () => {
            URL.revokeObjectURL(objectUrl);
            setUrl("");
        };
    }, [file]);

    return url;
};

// The permission step between the upload and the attach. By the time this
// opens the file is already in storage — nothing is attached to the invoice
// yet, and closing the dialog leaves the invoice as it was.
const InvoiceConfirmModal = ({ open, staged, isReplacing, invoiceRef, saving, onConfirm, onCancel }) => {
    const isImage = staged?.previewKind === PREVIEW_KIND.IMAGE;
    const previewUrl = useObjectUrl(isImage ? staged?.file : null);

    return (
        <Modal
            open={open}
            title={CONFIRM_TITLE}
            subtitle={invoiceRef ? `It will be attached to ${invoiceRef}.` : undefined}
            onClose={saving ? () => {} : onCancel}
            width="lg"
            footer={(
                <>
                    <LoadingButton
                        color="neutral"
                        size="md"
                        text="Cancel"
                        onClick={onCancel}
                        disabled={saving}
                        testId="cancel-attach-invoice" />
                    <LoadingButton
                        color="dark"
                        size="md"
                        text="Attach invoice"
                        loadingText="Attaching…"
                        loading={saving}
                        onClick={onConfirm}
                        testId="confirm-attach-invoice" />
                </>
            )}>
            {staged && (
                <div className="flex flex-col gap-5 sm:flex-row">
                    <div className="sm:w-[200px] sm:shrink-0">
                        <div className={FRAME_CLASS}>
                            {isImage && previewUrl ? (
                                <img
                                    src={previewUrl}
                                    alt={`Preview of ${staged.name}`}
                                    className="max-h-[220px] w-auto rounded bg-white shadow-sm" />
                            ) : (
                                <div className="flex flex-col items-center gap-2 px-2 text-center">
                                    <span className="grid h-10 w-10 place-items-center rounded-lg border border-gray-200 bg-white text-primary">
                                        <FileText className="h-5 w-5" />
                                    </span>
                                    <p className="text-xs leading-relaxed text-muted">
                                        Open the uploaded copy to check it before attaching.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="min-w-0 flex-1">
                        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                            <Detail label="File name" value={staged.name} />
                            <Detail label="File size" value={formatFileSize(staged.size)} />
                        </dl>

                        <a
                            href={staged.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 inline-flex items-center gap-1.5 text-sm text-gray-700 hover:text-primary hover:underline">
                            Open the uploaded copy
                            <ExternalLink className="h-3.5 w-3.5" />
                        </a>

                        <p className="mt-4 rounded-lg border border-gray-200 bg-light/40 p-3 text-xs leading-relaxed text-muted">
                            {isReplacing ? CONFIRM_NOTE.REPLACE : CONFIRM_NOTE.FIRST}
                        </p>
                    </div>
                </div>
            )}
        </Modal>
    );
};

InvoiceConfirmModal.propTypes = {
    open  : PropTypes.bool.isRequired,
    staged: PropTypes.shape({
        name       : PropTypes.string,
        size       : PropTypes.number,
        url        : PropTypes.string,
        file       : PropTypes.object,
        previewKind: PropTypes.oneOf(Object.values(PREVIEW_KIND))
    }),
    isReplacing: PropTypes.bool,
    invoiceRef : PropTypes.string,
    saving     : PropTypes.bool,
    onConfirm  : PropTypes.func.isRequired,
    onCancel   : PropTypes.func.isRequired
};

export default InvoiceConfirmModal;
